import type { Milestone } from '../types';
import { effectiveTimelineDeliveryStatus } from './timelineDeliveryPresentation';

const hasStarted = (milestone: Milestone) => Boolean(milestone.workStartedAt) ||
  !['not_started', undefined].includes(effectiveTimelineDeliveryStatus(milestone));

/** Read model while activation is pending. Explicit identity, never stage number. */
export function replacementTimelineProjection(current: Milestone[], replacement: Milestone[]) {
  const currentById = new Map(current.map(milestone => [milestone.id, milestone]));
  const sourceByTargetId = new Map<string, Milestone>();
  for (const target of replacement) {
    const source = target.replacesMilestoneId ? currentById.get(target.replacesMilestoneId) : undefined;
    if (source) sourceByTargetId.set(target.id, source);
  }
  const targetByStartedSourceId = new Map(replacement.flatMap(target => {
    const source = sourceByTargetId.get(target.id);
    return source && hasStarted(source) ? [[source.id, target.id] as const] : [];
  }));
  const milestones = replacement.map(target => {
    const source = sourceByTargetId.get(target.id);
    if (!source) return target;
    const predecessors = new Set<string>();
    const visited = new Set([source.id]);
    const visit = (id: string) => {
      if (visited.has(id)) return;
      visited.add(id);
      const survivingTargetId = targetByStartedSourceId.get(id);
      if (survivingTargetId) {
        predecessors.add(survivingTargetId);
      } else {
        // Keep the path through removed stages without attaching unrelated work.
        currentById.get(id)?.startPredecessorIds?.forEach(visit);
      }
    };
    source.startPredecessorIds?.forEach(visit);
    return {
      ...target,
      deliveryStatus: source.deliveryStatus,
      companyAttestation: source.companyAttestation,
      personAttestation: source.personAttestation,
      workStartedAt: source.workStartedAt,
      workStartedByRole: source.workStartedByRole,
      companyStartDecision: source.companyStartDecision,
      companyStartDecidedAt: source.companyStartDecidedAt,
      startPredecessorIds: [...predecessors],
      submittedAt: source.submittedAt,
      jointlyAttestedAt: source.jointlyAttestedAt,
      paidAt: source.paidAt,
      disputeReason: source.disputeReason,
      disputedAt: source.disputedAt,
      conflictStartedAt: source.conflictStartedAt
    };
  });
  // Actions still apply to the current agreement. Only expose them inline when
  // its scope and amount match what the user sees on the replacement card.
  const actionSources = new Map(replacement.flatMap(target => {
    const source = sourceByTargetId.get(target.id);
    return source && (source.itemType ?? 'milestone') === (target.itemType ?? 'milestone') &&
      source.description.trim() === target.description.trim() && source.amount === target.amount &&
      source.afterRotAmount === target.afterRotAmount
      ? [[target.id, source] as const] : [];
  }));
  return { milestones, actionSources };
}
